import MenuGroup from "aldoo-ra/MenuGroup"
import PubSub from "pubsub-js"
import Button from "aldoo-ra/Button"
import SiteLanguageSelector from "../SiteLanguageSelector/button"
import Localization from "aldoo-ra/Localization"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import DynamicSVG from "aldoo-ra/DynamicSVG"
import Icon from "aldoo-ra/Icon"
import { navigate } from "vike/client/router"
import useSitePlatform from "@components/Site/useSitePlatform"

const HeaderMenuDesktop = () => {
  const [platform, setPlatform] = useSitePlatform()

  const { template } = Localization()

  return (
    <div
      className="fixed flex bg-theme_white flex-row h-[80px] w-full top-0 left-0 items-center justify-between px-4 lg:px-6 xl:px-px1_xl"
      style={{ zIndex: 190 }}
    >
      {/* Header Logo */}
      <div className="w-[104px] xl:w-[140px] flex-shrink-0 pb-[10px] xl:pb-3 mr-2">
        <MenuGroup
          name="Header Logo"
          renderAs="hlist.icons"
          menuItemRender={(item) => (
            <img src={item.image} alt={item.label || "logo"} className="object-contain w-full h-auto" />
          )}
        />
      </div>

      {/* Central menu group */}
      <div className="flex items-center justify-center xl:gap-1 2xl:gap-3">
        {/* Header Products */}
        <MenuGroup
          name="Header Products"
          renderAs="panel"
          variant="aqua-panel-menu-lowercase"
          itemWidth="800px"
          itemsAsLinks={false}
          onItemClick={(item) => {
            // Parse the target URL to get the platform
            const targetPath = item.link[0].url
            const targetSegments = targetPath.split('/').filter(Boolean)
            // The platform is always the first segment (android, ios, windows)
            const urlPlatform = targetSegments[0]

            let currentUrl = window.location.href
            const pathname = window.location.pathname

            // Check if URL has language code
            const langMatch = pathname.match(/^\/([a-z]{2})(\/|$)/)
            const langPrefix = langMatch ? `/${langMatch[1]}` : ''

            //also set the platform from the link[0].url
            setPlatform(urlPlatform)
            //if the user is a at a feature page, redirect to the feature page
            //redirect to the same page for the current platform
            if (pathname.includes("features")) {
              // Get the path segment after language (if present) or first segment
              const pathSegments = pathname.split('/').filter(Boolean)
              const platformIndex = langMatch ? 1 : 0
              const currentPlatform = pathSegments[platformIndex]

              //replace the current platform with the new platform
              const newUrl = currentUrl.replace(
                `/${currentPlatform}`,
                `/${urlPlatform}`
              )

              //special case for advanced-features not available on ios
              //redirect to account-and-setup instead
              if (
                urlPlatform === "ios" &&
                pathname.includes("advanced-features")
              ) {
                const redirectUrl = newUrl.replace("advanced-features", "account-and-setup")
                navigate(redirectUrl.replace(window.location.origin, ''))
                return
              }

              //navigate to the new url
              navigate(newUrl.replace(window.location.origin, ''))
              return
            }
            //just navigate to the link[0].url when not in a feature page
            // Prepend language prefix to the target URL if needed
            const targetUrl = langPrefix ? `${langPrefix}${item.link[0].url}` : item.link[0].url
            navigate(targetUrl)
          }}
          rootItemRender={(item) => {
            return (
              <Button
                variant="nav_menuitem"
                className="flex items-center justify-center gap-[4px] px-0 xl:mr-4 2xl:mr-[68px]"
              >
                {item.label}
                <Icon id={platform} className="w-4 h-4" />

                <div className="w-5 h-5 flex items-center justify-center">
                  <ChevronDownIcon className="w-4 h-4" strokeWidth={3} />
                </div>
              </Button>
            )
          }}
        />

        {/* Features */}
        <MenuGroup
          name={"Header Features"}
          renderAs="panel"
          variant="aqua-panel-menu"
          preProcessItem={(item) => {
            if (item.items) {
              item.items = item.items
                .filter((item) => {
                  const { platforms } = item.options || {}
                  //filter out the items that don't have the platform in the options.platforms
                  //if the platform is all, return the item
                  if (!platforms) return true
                  //check if the current platform is in the platforms csv
                  return platforms.toLowerCase().includes(platform)
                })
                .map((i) => {
                  //replace the {{platform}} with the current platform inside the link[0].url
                  if (i.link && i.link[0] && i.link[0].url) {
                    i.link[0].url = template(i.link[0].url, { platform })
                  }
                  return i
                })
            }
            //return the modified item
            return item
          }}
          rootItemRender={(item) => {
            return (
              <Button
                variant="nav_menuitem"
                className="flex items-center justify-center gap-[4px] px-0"
              >
                {item.label}

                <div className="w-6 h-6 flex items-center justify-center">
                  <ChevronDownIcon className="w-4 h-4" strokeWidth={3} />
                </div>
              </Button>
            )
          }}
        />

        {/* Header Pricing */}
        <MenuGroup
          name="Header Pricing"
          renderAs="hlist"
          styleOverrides={{
            menu_item: "px-2 xl:px-3 2xl:px-4",
          }}
          itemWidth="auto"
          menuItemRender={(item) => {
            return (
              <Button
                variant="nav_menuitem"
                className="flex items-center justify-center px-0"
              >
                {item?.label}
              </Button>
            )
          }}
        />

        {/* Header Partners */}
        <MenuGroup
          name="Header Partners"
          renderAs="vlist.menu"
          itemWidth="auto"
          styleOverrides={{
            root_item: "flex items-center cursor-pointer pl-2 xl:pl-3 2xl:pl-4 pr-1 xl:pr-2 2xl:pr-3 py-2 rounded-md ",
          }}
          rootItemRender={(item) => {
            return (
              <Button
                variant="nav_menuitem"
                className="flex items-center justify-center gap-[4px] px-0"
              >
                {item.label}

                <div className="w-6 h-6 flex items-center justify-center">
                  <ChevronDownIcon className="w-4 h-4" strokeWidth={3} />
                </div>
              </Button>
            )
          }}
          menuItemRender={(item) => {
            return (
              <div className="flex flex-col gap-[8px] mb-2">
                <Button variant="nav_menuitem" className="text-left mb-[8px]">
                  {item.label}
                </Button>
              </div>
            )
          }}
        />

        {/* Header Resources */}
        <MenuGroup
          name="Header Resources"
          renderAs="vlist.menu"
          itemWidth="150px"
          styleOverrides={{
            root_item: "flex items-center cursor-pointer pl-2 xl:pl-3 2xl:pl-4 pr-1 xl:pr-2 2xl:pr-3 py-2 rounded-md ",
          }}
          maxItemsPerColumn={10}
          rootItemRender={(item) => {
            return (
              <Button
                variant="nav_menuitem"
                className="flex items-center justify-center gap-[4px] px-0"
              >
                {item.label}

                <div className="w-6 h-6 flex items-center justify-center">
                  <ChevronDownIcon className="w-4 h-4" strokeWidth={3} />
                </div>
              </Button>
            )
          }}
          menuItemRender={(item) => {
            return (
              <div className="flex flex-col gap-[8px] mb-2">
                <Button variant="nav_menuitem" className="text-left mb-[8px]">
                  {item.label}
                </Button>
              </div>
            )
          }}
        />
      </div>

      <div className="flex items-center justify-end 2xl:gap-3">
        <MenuGroup
          name="Header Download"
          renderAs="hlist"
          itemWidth="auto"
          itemsAsLinks={false}
          menuItemRender={(item) => {
            const url =
              (item?.link[0]?.parameters &&
                item?.link[0]?.parameters[platform]) ||
              item?.link[0]?.url
            return (
              <Button variant="primary_s">
                <a href={url} target="_blank">
                  {item?.label}
                </a>
              </Button>
            )
          }}
        />

        <MenuGroup
          name="Header Profile"
          renderAs="hlist.icons"
          actionMap={{
            signin: () =>
              PubSub.publish("signin", {
                redirect: "/account",
              }),
          }}
          menuItemRender={(item) => {
            return (
              <DynamicSVG
                url={item.image}
                className="w-10 h-10 text-primary hover:text-primary_hov transition"
              />
            )
          }}
        />

        <SiteLanguageSelector />
      </div>
    </div>
  )
}

export default HeaderMenuDesktop
